import type { FC, PropsWithChildren } from 'react';
import type { AnimationDefinition, PanInfo } from 'framer-motion';
import { useEffect } from 'react';
import {
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Box,
  Flex,
  useBreakpointValue,
  ModalCloseButton,
} from '@chakra-ui/react';
import { motion, useAnimationControls } from 'framer-motion';

const initialAnimation: AnimationDefinition = { y: 0, transition: { type: 'spring' } };

type ModalProps = PropsWithChildren & { isOpen: boolean; onClose: VoidFunction };

const Modal: FC<ModalProps> = ({ children, isOpen, onClose }) => {
  const controls = useAnimationControls();
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (isOpen) {
      controls.set({ y: '100%' });
      controls.start(initialAnimation);
    }
  }, [isOpen]);

  const handleDragEnd = (_: any, info: PanInfo) => {
    if (info.offset.y > 200) {
      onClose();
    } else {
      controls.start(initialAnimation);
    }
  };

  if (isMobile) {
    return (
      <ChakraModal key='x' isOpen={isOpen} trapFocus={false} motionPreset='none' onClose={onClose}>
        <ModalOverlay />
        <ModalContent
          as={motion.div}
          position='fixed'
          bottom='0'
          mb='0'
          borderTopRadius='2xl'
          maxW='100vw'
          maxH='80%'
          drag='y'
          dragConstraints={{ top: 0 }}
          initial={{ y: 0 }}
          animate={controls}
          exit={{ y: '100%' }}
          transition={{ type: 'spring' }}
          onDragEnd={handleDragEnd as any}
        >
          <ModalCloseButton />
          <Flex align='center' justify='center' my={5}>
            <Box mx='auto' w={10} h={1} bg='gray.300' borderRadius='full' cursor='grab' />
          </Flex>
          <ModalBody
            flex='1'
            overflowY='auto'
            overscrollBehavior='contain'
            px={4}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {children}
          </ModalBody>
        </ModalContent>
      </ChakraModal>
    );
  }

  return (
    <ChakraModal key='x' trapFocus={false} size={{ xs: 'xs', sm: 'md', md: '4xl' }} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody>{children}</ModalBody>
      </ModalContent>
    </ChakraModal>
  );
};

export default Modal;
